import { useEffect } from "react";
import { Button } from "reactstrap";

import { ConfigField, IConfig } from "Pages/Setup/Connect/connectShared";
import {
    getRampFields,
    getRampOauthRefreshTokenSuffix,
    SecondarySystemKey,
} from "Pages/Setup/Connect/connectData";
import styles from "Pages/Setup/Connect/Onboarding.module.css";

/** Browser-facing AuthService base */
function authServiceBase(environment: string | undefined): string {
    switch (environment) {
        case "uat":
            return "https://uat-hub.calance.us";
        case "demo":
            return "https://demo-hub.calance.us";
        case "prod":
            return "https://hub.calance.us";
        default:
            return "";
    }
}

interface Props {
    find: (suffix: string) => IConfig | undefined;
    onEdit: (suffix: string, secret: boolean, index: number) => void;
    onRefresh: (showErrorAlert?: boolean) => Promise<boolean>;
    companyId: string | undefined;
    environment: string | undefined;
    secondarySystem: SecondarySystemKey;
    secondarySystemName: string;
    goTo: (view: string) => void;
}

export default function RampConnect({
    find,
    onEdit,
    onRefresh,
    companyId,
    environment,
    secondarySystem,
    secondarySystemName,
    goTo,
}: Props) {
    const rampFields = getRampFields(secondarySystem);
    const uiUrl = find("ramp.ui_url")?.keyValue ?? "";
    const demoTenant = /demo\.ramp\.com/i.test(uiUrl);
    const requiresDemoCredentials =
        (environment === "demo" || environment === "uat") && (demoTenant || !uiUrl);
    const demoCredentialFields = rampFields.filter(
        (f) =>
            f.suffix === `ramp.${secondarySystem}.client_id` ||
            f.suffix === `ramp.${secondarySystem}.client_secret`
    );
    const requiredMissing = demoCredentialFields.filter(
        (f) => !find(f.suffix)?.keyValue
    ).length;
    const credsReady = !requiresDemoCredentials || requiredMissing === 0;
    const oauthConnected = !!find(getRampOauthRefreshTokenSuffix(secondarySystem))?.keyValue;

    // Local dev: a locally-run AuthService (PR #818) listens on :5099. In
    // deployed envs use the public AuthService host.
    const localDev =
        typeof window !== "undefined" &&
        window.location.hostname === "localhost";
    const authBase = localDev
        ? "http://localhost:5099"
        : authServiceBase(environment);
    const authorizeUrl = authBase
        ? `${authBase}/oauth/ramp/${secondarySystem}/authorize?company_id=${encodeURIComponent(
              companyId ?? ""
          )}`
        : "";

    useEffect(() => {
        if (oauthConnected) return;

        const refreshOAuthStatus = () => {
            void onRefresh(false);
        };

        const onVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                refreshOAuthStatus();
            }
        };

        window.addEventListener("focus", refreshOAuthStatus);
        document.addEventListener("visibilitychange", onVisibilityChange);

        return () => {
            window.removeEventListener("focus", refreshOAuthStatus);
            document.removeEventListener("visibilitychange", onVisibilityChange);
        };
    }, [oauthConnected, onRefresh]);

    return (
        <div className={styles.panel}>
            <div className={styles.phead}>
                <div className={`${styles.logo} ${styles.logoRamp}`}>R</div>
                <div>
                    <div className={styles.pheadName}>Ramp</div>
                    <div className={styles.pheadSub}>
                        Spend management · cards, bills, reimbursements
                    </div>
                </div>
                {oauthConnected ? (
                    <span className={`${styles.pill} ${styles.pillGood}`}>
                        <span className={styles.dot} /> Connected
                    </span>
                ) : credsReady ? (
                    <span className={`${styles.pill} ${styles.pillWarn}`}>
                        <span className={styles.dot} /> Credentials set —
                        authorize to finish
                    </span>
                ) : (
                    <span className={`${styles.pill} ${styles.pillBad}`}>
                        <span className={styles.dot} /> Not connected
                    </span>
                )}
            </div>

            <div className={styles.tabs}>
                <button className={`${styles.tab} ${styles.tabActive}`}>
                    Connect
                </button>
                <button className={styles.tab} onClick={() => goTo("ramptest")}>
                    Test Connection
                </button>
            </div>

            <div className={styles.wz}>
                <div className={styles.wzLeft}>
                    <h2>Authorize Dimension in Ramp</h2>
                    {requiresDemoCredentials ? (
                        <>
                            <p className={styles.lead}>
                                Connect via Ramp OAuth. Manual app creation and Client ID/Secret entry is
                                required for companies using a demo instance of Ramp.
                                (i.e. https://demo.ramp.com).
                            </p>

                            <ol className={styles.steps}>
                                <li>
                                    In Ramp's left bar: {" "}
                                    <strong>Company › Developer › My apps</strong>.
                                </li>
                                <li>
                                    Create an app called {" "}
                                    <strong>{secondarySystemName} Integration</strong> and enable all
                                    grants: Authorization Code, Client Credentials, and
                                    Refresh Token.
                                </li>
                                <li>
                                    Copy the Client ID and Client Secret below.
                                </li>
                            </ol>
                            {
                                demoCredentialFields.map((field, index) => (
                                <ConfigField
                                    key={field.suffix}
                                    field={field}
                                    config={find(field.suffix)}
                                    index={index}
                                    onEdit={onEdit}
                                />
                                ))
                            }
                        </>
                    ) : (
                        <p className={styles.lead}>
                            Click the "Connect with Ramp" button to authorize the integration in Ramp. You must be an administrator within your Ramp account.
                        </p>
                    )}

                    <div className={styles.oauthBox}>
                        <div className={styles.oauthText}>
                            {oauthConnected ? (
                                <span className={styles.oauthOn}>
                                    ✓ Authorized via Ramp OAuth
                                </span>
                            ) : (
                                <span className={styles.oauthOff}>
                                    Finish by authorizing Dimension in Ramp
                                    (opens Ramp's consent screen).
                                </span>
                            )}
                        </div>
                        <Button
                            color="success"
                            disabled={!authorizeUrl || !credsReady}
                            title={
                                !authorizeUrl
                                    ? "OAuth is not available in this environment"
                                    : !credsReady
                                    ? "Set the Client ID and Secret first"
                                    : undefined
                            }
                            onClick={() => {
                                if (authorizeUrl) {
                                    window.open(authorizeUrl, "_blank", "noopener,noreferrer")?.focus();
                                    setTimeout(() => {
                                        void onRefresh(false);
                                    }, 800);
                                }
                            }}
                        >
                            {oauthConnected
                                ? "Reconnect with Ramp"
                                : "Connect with Ramp"}
                        </Button>
                    </div>
                </div>

                {requiresDemoCredentials && (
                    <div className={styles.wzRight}>
                        <p className={styles.rlabel}>Where to do this in Ramp</p>
                        <p className={styles.crumb}>
                            <strong>Company &gt; Developer &gt; My apps</strong>
                        </p>
                        <div className={styles.shot}>
                            <div className={styles.shotBar}>
                                <i />
                                <i />
                                <i />
                                <span className={styles.shotUrl}>
                                    demo.ramp.com › Developer › My apps
                                </span>
                            </div>
                            <div className={styles.shotBody}>
                                <div className={styles.shotStep}>
                                    <span className={styles.shotBadge}>1</span>
                                    <div>
                                        <b>Enable required scopes</b>
                                        <span>
                                            accounting:read/write, bills:read/write,
                                            custom_records:read/write, entities:read,
                                            receipts:read,
                                            reimbursements:read, transactions:read,
                                            transfers:read,
                                            users:read, vendors:read/write.
                                        </span>
                                    </div>
                                </div>
                                <div className={styles.shotStep}>
                                    <span className={styles.shotBadge}>2</span>
                                    <div>
                                        <b>Allow redirect URIs</b>
                                        <span>
                                            {`https://uat-hub.calance.us/oauth/ramp/${secondarySystem}/token and https://hub.calance.us/oauth/ramp/${secondarySystem}/token`}
                                        </span>
                                    </div>
                                </div>
                                <div className={styles.shotStep}>
                                    <span className={styles.shotBadge}>3</span>
                                    <div>
                                        <b>Copy and Paste Client Credentials</b>
                                        <span>
                                            Enter Client ID + Client Secret in the input fields.
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className={styles.footbar}>
                <Button color="link" onClick={() => goTo("secondary")}>
                    Set up {secondarySystemName} next →
                </Button>
                <span className={styles.saveState}>
                    {oauthConnected
                        ? "✓ Ramp is connected."
                        : credsReady
                        ? "Click Connect with Ramp to finish authorization."
                        : "Set the demo Ramp Client ID and Secret to begin."}
                </span>
            </div>
        </div>
    );
}
